import {
  toPersianDigits,
  toLatinDigits,
  toPersianNumericDisplay,
  parsePersianNumber,
  formatMileage,
} from "../Page/LatestServices/components/jalaliUtils";

export {
  toPersianDigits,
  toLatinDigits,
  toPersianNumericDisplay,
  parsePersianNumber,
  formatMileage,
};

// Storage: frontend-only mirror of the Next Service Mileage, keyed per
// vehicle+category. This keeps the value available for future notifications
// until a dedicated backend column exists. The canonical per-registration
// copy also travels inside the existing `details` JSON of POST /service
// (backend persists `details` untouched — no backend change required).
//
// Future backend integration: replace `loadAll`/`save` internals with
// GET/POST calls; keep these function signatures stable.
const STORAGE_KEY = "tasmeh.nextServiceMileage.v1";

export const DETAILS_KEYS = {
  nextServiceMileage: "nextServiceMileage",
  // Alias kept so backend-derived `nextDueKm` consumers keep working.
  nextDueKm: "nextDueKm",
};

const safeParse = (raw) => {
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const readStore = () => {
  try {
    if (typeof localStorage === "undefined") return {};
    return safeParse(localStorage.getItem(STORAGE_KEY));
  } catch {
    return {};
  }
};

const writeStore = (store) => {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Non-blocking: quota / private mode must never break registration.
  }
};

export const makeEntryKey = (vehicleId, categoryId) =>
  `${vehicleId}::${categoryId}`;

/**
 * Notification condition: due when current mileage reaches or passes
 * the Next Service Mileage. Pure + reusable by any page.
 */
export function isNextServiceMileageDue(currentMileage, nextServiceMileage) {
  const current = Number(currentMileage);
  const next = Number(nextServiceMileage);
  if (!Number.isFinite(current) || !Number.isFinite(next)) return false;
  if (next <= 0) return false;
  return current >= next;
}

export function getNextServiceStatus(currentMileage, nextServiceMileage, now = new Date()) {
  void now;
  if (nextServiceMileage === null || nextServiceMileage === undefined || nextServiceMileage === "") {
    return { state: "unset", isDue: false, remainingKm: null };
  }
  const current = Number(currentMileage);
  const next = Number(nextServiceMileage);
  if (!Number.isFinite(next) || next <= 0) return { state: "invalid", isDue: false, remainingKm: null };
  if (!Number.isFinite(current) || current < 0) return { state: "unknown", isDue: false, remainingKm: null };
  if (current >= next) return { state: "due", isDue: true, remainingKm: 0, overdueKm: current - next };
  return { state: "upcoming", isDue: false, remainingKm: next - current };
}

/** Interval (km) that maps an explicit next mileage onto the existing backend reminder model. */
export function computeIntervalKm(serviceMileage, nextServiceMileage) {
  const from = Number(serviceMileage);
  const to = Number(nextServiceMileage);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
  const diff = to - from;
  return diff > 0 ? diff : null;
}

/** Merge the explicit value into the existing `details` JSON contract (no new backend fields). */
export function buildDetailsWithNextServiceMileage(details = {}, nextServiceMileage) {
  const base = details && typeof details === "object" ? { ...details } : {};
  if (nextServiceMileage === null || nextServiceMileage === undefined || nextServiceMileage === "") {
    return base;
  }
  const n = Number(nextServiceMileage);
  if (!Number.isFinite(n) || n < 0) return base;
  return {
    ...base,
    [DETAILS_KEYS.nextServiceMileage]: n,
    [DETAILS_KEYS.nextDueKm]: n,
  };
}

/** Read the value back from a persisted service log (`details` JSON). */
export function getNextServiceMileageFromDetails(details) {
  if (!details || typeof details !== "object") return null;
  for (const key of [DETAILS_KEYS.nextServiceMileage, DETAILS_KEYS.nextDueKm]) {
    const n = Number(details[key]);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

export function saveNextServiceMileage({ vehicleId, categoryId, nextServiceMileage, serviceMileage = null }) {
  if (vehicleId === null || vehicleId === undefined || categoryId === null || categoryId === undefined) return null;
  const n = Number(nextServiceMileage);
  if (!Number.isFinite(n) || n <= 0) return null;
  const store = readStore();
  const entry = {
    vehicleId: Number(vehicleId),
    categoryId: Number(categoryId),
    nextServiceMileage: n,
    serviceMileage: serviceMileage === null || serviceMileage === undefined ? null : Number(serviceMileage),
    updatedAt: new Date().toISOString(),
  };
  store[makeEntryKey(entry.vehicleId, entry.categoryId)] = entry;
  writeStore(store);
  return entry;
}

export function getNextServiceMileage({ vehicleId, categoryId }) {
  if (vehicleId === null || vehicleId === undefined || categoryId === null || categoryId === undefined) return null;
  const store = readStore();
  return store[makeEntryKey(Number(vehicleId), Number(categoryId))] || null;
}

export function clearNextServiceMileage({ vehicleId, categoryId }) {
  const store = readStore();
  delete store[makeEntryKey(Number(vehicleId), Number(categoryId))];
  writeStore(store);
}

export function loadAllNextServiceMileages() {
  return Object.values(readStore()).filter(
    (e) => e && Number.isFinite(Number(e.nextServiceMileage)) && Number(e.nextServiceMileage) > 0
  );
}

/**
 * Adapt a frontend-only entry to the backend ServiceReminder shape so the
 * existing NotificationCard / sorting logic renders it unchanged.
 * `vehicle` should carry at least { id, title, currentMileage }.
 */
export function toReminderShape(entry, { vehicle = null, category = null } = {}) {
  if (!entry) return null;
  const next = Number(entry.nextServiceMileage);
  if (!Number.isFinite(next) || next <= 0) return null;
  const vehicleId = Number(entry.vehicleId ?? vehicle?.id ?? NaN);
  const categoryId = Number(entry.categoryId ?? category?.id ?? NaN);
  return {
    id: `local-${vehicleId}-${categoryId}`,
    vehicleId: Number.isFinite(vehicleId) ? vehicleId : entry.vehicleId,
    categoryId: Number.isFinite(categoryId) ? categoryId : entry.categoryId,
    intervalKm: entry.serviceMileage != null ? computeIntervalKm(entry.serviceMileage, next) : null,
    intervalMonths: null,
    lastServiceKm: entry.serviceMileage ?? null,
    lastServiceDate: entry.updatedAt ?? null,
    nextDueKm: next,
    nextDueDate: null,
    isActive: true,
    isLocal: true,
    vehicle: vehicle || { id: entry.vehicleId, title: "خودرو", currentMileage: null },
    category: category || { id: entry.categoryId, title: "سرویس دوره‌ای" },
  };
}
